import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PageTitle, StatusBadge, TextArea } from "./shared";

const CustomerRow = ({ customer }) => {
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const [notes, setNotes] = useState(customer.notes || "");

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && orders === null) {
      const { data } = await api.get(`/admin/customers/${encodeURIComponent(customer.email)}/orders`);
      setOrders(data);
    }
  };

  const saveNotes = async () => {
    await api.patch(`/admin/customers/${encodeURIComponent(customer.email)}`, { notes });
    toast.success("Customer notes saved");
  };

  return (
    <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.25rem] overflow-hidden" data-testid={`customer-row-${customer.email}`}>
      <button onClick={toggle} className="w-full flex items-center gap-3 px-5 py-4 text-left" data-testid={`customer-toggle-${customer.email}`}>
        <span className="flex-1 min-w-0">
          <span className="block font-medium text-sm truncate">{customer.name}</span>
          <span className="block text-xs text-plum-deep/60 truncate">{customer.email}{customer.phone ? ` · ${customer.phone}` : ""}</span>
        </span>
        <span className="text-sm text-plum-deep/70 hidden sm:block">{customer.orders_count || 0} orders</span>
        <span className="text-sm font-semibold">${(customer.total_spent || 0).toFixed(2)}</span>
        <span className="text-xs text-plum-deep/50 hidden md:block">{customer.last_order_at ? new Date(customer.last_order_at).toLocaleDateString("en-NZ") : "—"}</span>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-plum-deep/10 pt-4 grid md:grid-cols-2 gap-5" data-testid={`customer-detail-${customer.email}`}>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">ORDER HISTORY</p>
            {orders === null ? (
              <p className="text-sm text-plum-deep/60">Loading…</p>
            ) : orders.length === 0 ? (
              <p className="text-sm text-plum-deep/60">No orders found.</p>
            ) : (
              <ul className="space-y-2">
                {orders.map((o) => (
                  <li key={o.id} className="flex items-center gap-3 bg-cream rounded-xl px-3 py-2.5 text-sm">
                    <span className="font-semibold">{o.order_number}</span>
                    <span className="text-xs text-plum-deep/50 flex-1">{new Date(o.created_at).toLocaleDateString("en-NZ")}</span>
                    <span>${o.total?.toFixed(2)}</span>
                    <StatusBadge status={o.status} />
                  </li>
                ))}
              </ul>
            )}
            {customer.address && <p className="mt-3 text-xs text-plum-deep/60">Address: {customer.address}</p>}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">PRIVATE NOTES</p>
            <TextArea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. loves plum tags, repeat buyer, gift wrap orders…" data-testid={`customer-notes-${customer.email}`} />
            <button onClick={saveNotes} className="mt-2 inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-plum" data-testid={`customer-save-notes-${customer.email}`}>
              <Save className="w-3.5 h-3.5" /> SAVE NOTES
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerDatabase = () => {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  }, []);

  const filtered = (customers || []).filter(
    (c) => !search || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="console-customers">
      <PageTitle kicker="CRM" title="CUSTOMERS">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customers…"
          data-testid="customer-search"
          className="rounded-full px-5 py-2.5 bg-warmwhite border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta w-56"
        />
      </PageTitle>
      {customers === null ? (
        <p className="text-plum-deep/60">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-plum-deep/60" data-testid="customers-empty">No customers found.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => <CustomerRow key={c.email} customer={c} />)}
        </div>
      )}
    </div>
  );
};

export default CustomerDatabase;
